<template>
  <div v-if="data" class="relative h-full pb-4 overflow-x-auto bg-white">
    <table class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
      <tbody>
        <BaseDetailsTableRow name="BOID" :mode="mode" :isHorizontalLayout="isHorizontalLayout">
          {{ data.boid }}
        </BaseDetailsTableRow>
        <BaseDetailsTableRow
          :name="$t('baseObject.code')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.code }}
        </BaseDetailsTableRow>
        <BaseDetailsTableRow
          :mode="mode"
          :name="$t('baseObject.name')"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.name || '-' }}
        </BaseDetailsTableRow>
        <BaseDetailsTableRow
          :name="$t('traderSites.traderName')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.trader?.name || '-' }}
        </BaseDetailsTableRow>
        <BaseDetailsTableRow
          :name="$t('traderSites.companySite')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.companySite?.name || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :mode="mode"
          :name="$t('address')"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.address }}
          <span v-if="data.zipCode || data.city">, {{ data.city }} {{ data.zipCode }}</span>
        </BaseDetailsTableRow>
        <BaseDetailsTableRow
          :mode="mode"
          :name="$t('telephone')"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.telephone1 }}
        </BaseDetailsTableRow>
        <BaseDetailsTableRow :mode="mode" name="Email" :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.email"
            color="base"
            flat
            type="text"
            show-error-as-tooltip
            :error="validation.email.email.$invalid ? validation.email.email.$message : ''"
            :disabled="mode == 'view'" />
        </BaseDetailsTableRow>
        <BaseDetailsTableRow
          :mode="mode"
          :name="$t('vatLevel')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :mode="mode"
            name=""
            flat
            :items="dropdownVatLevels"
            :selected-item="selectedVatLevel"
            clearable
            show-error-as-tooltip
            :error="
              validation.vatLevel.required.$invalid ? validation.vatLevel.required.$message : ''
            "
            @update:selected="handleVatLevelSelection" />
        </BaseDetailsTableRow>
        <BaseDetailsTableRow
          :mode="mode"
          :name="$t('traderSites.latitude')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.latitude"
            color="base"
            flat
            type="number"
            :disabled="mode == 'view'" />
        </BaseDetailsTableRow>
        <BaseDetailsTableRow
          :mode="mode"
          :name="$t('traderSites.longitude')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.longitude"
            color="base"
            flat
            type="number"
            :disabled="mode == 'view'" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isActive')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellStatus :item="data.isActive ? data.isActive : 0" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow :name="$t('HQ')" :mode="mode" :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellCheck :item="data.isHQ ? data.isHQ : false" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('branch')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellCheck :item="data.isBranch ? data.isBranch : false" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isFromManager')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellCheck :item="data.isFromManager ? data.isFromManager : false" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.lastUpdate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ isoDateToFullDate(data.lastUpdate) || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.createdDate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ isoDateToFullDate(data.createdDate) || '-' }}
        </BaseDetailsTableRow>
        <BaseDetailsTableRow
          :name="$t('traderSites.storePhoto')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout"
          @dragstart.prevent
          @drop.prevent>
          <ImageUpload
            v-if="mode == 'edit'"
            :image="storePhoto"
            class="my-4"
            @image-changed="onStorePhotoChange" />
          <span v-else><img v-if="storePhoto" :src="storePhoto" /></span>
        </BaseDetailsTableRow>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';

import { isoDateToFullDate } from '@/utils/date';
import BaseTableCellCheck from '../common/BaseTableCellCheck.vue';
import BaseTableCellStatus from '../common/BaseTableCellStatus.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import BaseDetailsTableRow from '../common/BaseDetailsTableRow.vue';
import ImageUpload from '../common/ImageUpload.vue';
import type { TraderSite } from '@/types/Traders';
import { convertBlobToBase64 } from '@/utils/image';
import { type SelectDropdownItem } from '@/types/Base';

import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';
import type { Validation } from '@vuelidate/core/index.js';

defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout: boolean;
  validation: Validation;
}>();

const data = defineModel<TraderSite>();

const storePhoto = ref<string>('');

const { dropdownVatLevels } = storeToRefs(useLookupsStore());
const { getVatLevelsLookup } = useLookupsStore();

onMounted(async () => {
  if (data.value && data.value.storePhoto)
    storePhoto.value = `data:image/jpeg;base64,${data.value?.storePhoto}`;

  await getVatLevelsLookup();
  initDropDowns();
});

function initDropDowns() {
  if (data.value && data.value.vatLevel) {
    const vatLevelValue = dropdownVatLevels.value.find(
      (item) => item.value == data.value?.vatLevel?.id
    );
    if (vatLevelValue) {
      selectedVatLevel.value = vatLevelValue;
      data.value.vatLevelId = data.value.vatLevel.id;
    }
  }
}

const selectedVatLevel = ref<SelectDropdownItem | null>(null);

function handleVatLevelSelection(item: SelectDropdownItem) {
  selectedVatLevel.value = item ? item : null;
  if (data.value) {
    data.value.vatLevelId = item ? item.value.toString() : undefined;
    data.value.vatLevel = item
      ? {
          id: item.value.toString(),
          name: item.name,
        }
      : undefined;
  }
}

function onStorePhotoChange(image: string) {
  if (!data.value) return;
  if (!image) data.value.storePhoto = undefined;
  if (image && image.startsWith('blob')) {
    convertBlobToBase64(image)
      .then((base64Image) => {
        if (data.value)
          data.value.storePhoto = base64Image.replace(/^data:image\/[a-zA-Z]+;base64,/, '');
      })
      .catch((error) => {
        console.error('Error converting blob to Base64:', error);
      });
  } else if (image && image.startsWith('data:image/,') && image.endsWith(';base64,')) {
    data.value.storePhoto = image.replace(/^data:image\/[a-zA-Z]+;base64,/, '');
  }
}

watch(
  () => data.value,
  () => {
    initDropDowns();
    if (data.value && data.value.storePhoto)
      storePhoto.value = `data:image/jpeg;base64,${data.value?.storePhoto}`;
    else storePhoto.value = '';
  },
  { deep: true }
);
</script>
